import { defineStore } from 'pinia';

/** Filter toggles for the intent overview hub list */
export interface HublistFilter {
  /** Show regular intents */
  intents: boolean;
  /** Show intent suggestions */
  suggestion: boolean;
  /** Show smalltalk intents */
  smalltalks: boolean;
  /** Show only intents that are in review */
  inReview: boolean;
}

/** State of the behaviour module (UI behaviour persisted per session) */
export interface BehaviourState {
  /** Saved scroll offset (px) of the intent overview list */
  intentOverviewScrollPosition: number;
  /** Active filters of the intent overview hub list */
  hublistFilter: HublistFilter;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/behaviour.ts` (namespace
 * `behaviour`). Persisted to `sessionStorage` (full state) — matches the legacy `vuexSession`
 * reducer's `behaviour: state.behaviour` entry in `src/store/index.ts`.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Getters `intentOverviewScrollPosition` and `hublistFilter` were pure identity passthroughs of
 *   the state props of the same name. Pinia exposes state props directly on the store instance
 *   (`useBehaviourStore().intentOverviewScrollPosition`, `useBehaviourStore().hublistFilter`), so
 *   these getters are DROPPED entirely rather than renamed — consumers read the state prop
 *   directly, which is exactly what the old getter returned. (Same precedent as
 *   `src/stores/app.ts`'s `globalSnackbar`/`connectionLost`/`appVersion`.)
 * - `setIntentOverviewScrollPosition` and `setHublistFilters` each existed as BOTH an action
 *   (which only committed the mutation of the same name) and a mutation of the same name — merged
 *   into single same-named actions.
 */
export const useBehaviourStore = defineStore('behaviour', {
  state: (): BehaviourState => ({
    intentOverviewScrollPosition: 0,
    hublistFilter: {
      intents: true,
      suggestion: false,
      smalltalks: true,
      inReview: false,
    },
  }),
  actions: {
    /**
     * Merged from legacy action `setIntentOverviewScrollPosition` (which only committed the
     * mutation of the same name) + legacy mutation `setIntentOverviewScrollPosition`.
     */
    setIntentOverviewScrollPosition(position: number) {
      this.intentOverviewScrollPosition = position;
    },
    /**
     * Merged from legacy action `setHublistFilters` (which only committed the mutation of the
     * same name) + legacy mutation `setHublistFilters`.
     */
    setHublistFilters(filters: Partial<HublistFilter>) {
      if (filters) {
        Object.keys(filters).forEach((key) => {
          (this.hublistFilter as any)[key] = (filters as any)[key];
        });
      }
    },
  },
  vuexPersist: {
    session: true,
  },
});
